import React, { ChangeEvent, useRef } from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Billing } from 'models/BillingModel';
import AsyncSelect from 'react-select/async';
import { Client } from 'models/ClientModel';
import { useSearchParams } from 'react-router-dom';

const BillingForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.BILLING, Code.Form.Name.BILLING];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [insurerOptions, setInsurerOptions] = Hooks.Form.Options();
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [hscOptions, setHscOptions] = Hooks.Form.OptionMore();
  const [diagOptions, setDiagOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Billing>({});
  const [clientData, setClientData] = useState<Client>({});
  const [selectedHsc, setSelectedHsc] = useState<number | null>(null);

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
  const doctorId = searchParams.get(Code.UrlPara.Doctor);
  const examId = searchParams.get(Code.UrlPara.Exam);
  const examDate = searchParams.get(Code.UrlPara.Date);
  
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.DOCTOR, Code.Option.Common.SELECT, setDoctorOptions, setError);
    Files.Json.API(Code.File.INSURER, Code.Option.Common.NA, setInsurerOptions, setError);
    Files.Json.HttpMore(Code.File.HSC, Code.Option.Common.SELECT, setHscOptions, setError);
    Files.Json.Http(Code.File.DIAG, Code.Option.Common.SELECT, setDiagOptions, setError);
    Files.Json.Http(Code.File.CLAIM+Code.File.STATUS, Code.Option.Common.SELECT, setStatusOptions, setError);

    if(keyId !== Code.Key.NEW) {
      API.Data.GetByKeyId<Billing>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.billing_active); 
        const client = await API.Data.GetByKeyId<Client>(Code.Form.Name.CLIENT, data.client_id, setLoading, setError);
        if(client){
          setClientData(client);
        }
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
          client_name: Utility.Get.ClientName(client, Code.Format.Client.ADDRESS_PHONE), 
          client_number: client?.client_number, 
          client_dob: client?.client_dob, 
        }));
      }})
      setLoading(false);
    } else {
      setFormData((prevData) => ({
        ...prevData,
        status_id: Code.Option.Billing.TOSUBMIT,
      }));
      setLoading(false); 
    }

    const onNewEntry = async () => {
      if(keyId===Code.Key.NEW) {
        //console.log("New billing entry ExamDate:", examDate);
        const clientName = await API.Data.GetClientName(Number(clientId));
        setFormData((prevData) => ({
          ...prevData,
          status_id: Code.Option.Billing.TOSUBMIT,
          doctor_id: doctorId ? Number(doctorId) : 2,
          exam_id: examId ? Number(examId) : 2,
          billing_date: examDate ? new Date(`${examDate} 10:00:00`) : new Date(),
          client_id: Number(clientId),
          client_name: Utility.Value.Text(clientName), 
        }));
      }
    };
    onNewEntry();

  }, []);

  useEffect(() => {
    getHscPrice();
  }, [selectedHsc, formData.client_dob]);

  useEffect(() => {
    const fetchClientDob = async () => {
      const clientId = formData.client_id;
      if (clientId) {
        API.Data.GetByKeyId<Client>(Code.Form.Name.CLIENT, clientId.toString(), setLoading, setError)
          .then(async(data) => { 
            if(data) {
              //console.log("Client DOB:", data.client_dob);
              setFormData((prev: any) => ({
                ...prev,
                client_dob: data.client_dob,
              }));
            }
          });
      }
    };
  
    fetchClientDob();
  }, [formData.client_id]);

  const getHscPrice = () => {
    if (!selectedHsc || !formData.client_dob) return;

      const selected = hscOptions.find(option => option.value === selectedHsc);
      if (!selected) return;

      const dob = formData?.client_dob;
      const isChild = dob && Utility.isValid.ChildAge(dob);

      const HscRate = isChild ? selected.data3 : selected.data2;

      setFormData((prev: any) => ({
        ...prev,
        billing_price: HscRate, 
        billing_quantity: 1,
        billing_amount: HscRate, 
      }));
  }

  const calculateFunction = (updatedFormData: any) => {
      let billingPrice = Number(updatedFormData.billing_price) || 0;
      let billingQuantity = Number(updatedFormData.billing_quantity) || 0;
      let billingAmount = Number(updatedFormData.billing_amount) || 0;
      
      billingAmount = billingPrice * billingQuantity;

      return {
        ...updatedFormData,
        billing_amount: Utility.Maths.toDecimal(billingAmount),
      };
    };

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
      billing_action: Code.Option.ActionAHS.Add,
      billing_id: formData.billing_id ?? 2,
    });

    console.log("Final data to submit:", finalData);

    const response = await API.Data.Save(formName, keyId, finalData, setMessage)
    if(response){
      //console.log("response---------->",response);
    }
  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} clientNumber={clientData.client_number} formName={formName} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details","More"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
            
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Date id={Name.Billing.BillingDate} value={formData.billing_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,33,1]} require={true} currentDate={true} />
                <Field.Select id={Name.Doctor.Id} value={formData.doctor_id} title={Title.Doctor} options={doctorOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} require={true} />
                <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.Status} options={statusOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,33,0]} selected={10} disabled={true} />
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
              </Control.Row>
              <Control.Box>
                <Field.FilterSelect id={Name.Billing.Hsc} value={formData.billing_hsc_id} title={Title.HSC} options={hscOptions} setFormData={setFormData} require={true} setSelectedId={setSelectedHsc} />
              </Control.Box>
              <Control.Row>
                <Field.Amount id={Name.Billing.Price} value={formData.billing_price} title={Title.Price} onChange={(e) => Utility.Handle.Calculate(e,setFormData,calculateFunction)} size={[0,33,1]} require={true} />
                <Field.Number id={Name.Billing.Quantity} value={formData.billing_quantity} title={Title.Quantity} onChange={(e) => Utility.Handle.Calculate(e,setFormData,calculateFunction)} size={[1,34,1]} require={true} minValue={1} defaultValue={1} disabled={true} />
                <Field.Amount id={Name.Billing.Amount} value={formData.billing_amount} title={Title.Amount} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,33,0]} require={true} disabled={true} />
              </Control.Row>
              <Control.Box>
                <Field.FilterSelect id={Name.Billing.Diagnosis1} value={formData.billing_diagnosis1_id} title={Title.Diagnosis} options={diagOptions} setFormData={setFormData} />
                <Field.FilterSelect id={Name.Billing.Diagnosis2} value={formData.billing_diagnosis2_id} options={diagOptions} setFormData={setFormData} />
                <Field.FilterSelect id={Name.Billing.Diagnosis3} value={formData.billing_diagnosis3_id} options={diagOptions} setFormData={setFormData} />
              </Control.Box>
              {/* <Control.Row>
                <Field.Amount id={Name.Billing.Taxable} value={formData.billing_taxable} title={Title.Taxable} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,33,1]} require={true} />
                <Field.Amount id={Name.Billing.TaxAmount} value={formData.billing_tax_amount} title={Title.Tax} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,33,1]} require={true} />
                <Field.Amount id={Name.Billing.Amount} value={formData.billing_amount} title={Title.Billed} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,33,0]} require={true} />
              </Control.Row> */}
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <Control.Row>
                <Field.Text id={Name.Billing.RefNo} value={formData.billing_ref_number} title={Title.Refno} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Select id={Name.Insurer.Id} value={21} title={Title.Insurer} options={insurerOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} disabled={true} />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Billing.Comment} value={formData.billing_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Billing.Modifier1} value={formData.billing_modifier1} title="Modifier 1" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,33,1]} />
                <Field.Text id={Name.Billing.Modifier2} value={formData.billing_modifier2} title="Modifier 2" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,34,1]} />
                <Field.Text id={Name.Billing.Modifier3} value={formData.billing_modifier3} title="Modifier 3" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,33,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Billing.ExplanatoryCode} value={formData.billing_explanatory_code} title="Explanatory code" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Billing.GovKey} value={formData.billing_govkey} title="Gov key" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Date id={Name.Billing.ClaimDate} value={formData.claim_date} title="Claim Date" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Date id={Name.Billing.RemittanceDate} value={formData.remittance_date} title="Remittance Date" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Billing.History} value={formData.billing_history} title={Title.History} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
            </Control.Tab.Content>
            
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default BillingForm;